<template>
  <div>
    <!-- Loading: the shape of the hero and the ladder, not a spinner over a
         black screen. -->
    <template v-if="loading">
      <!-- Shaped to the loaded hero line for line, so nothing jumps when the
           figures arrive. -->
      <div class="lvl-skelHero sk-dark">
        <div class="lvl-skelRow">
          <span class="sk sk-circle" style="flex: 0 0 68px; height: 68px"></span>
          <span class="sk"></span>
        </div>
        <span class="sk lvl-skelToGo"></span>
        <span class="sk lvl-skelBar"></span>
        <span class="sk lvl-skelEnds"></span>
      </div>

      <div class="lvl-sectionHd"><h2>Level badges</h2></div>
      <ul class="lvl-grid sk-dark">
        <li v-for="n in 6" :key="n" class="lvl-tier">
          <span class="sk lvl-skelTile"></span>
          <span class="sk lvl-skelRange"></span>
        </li>
      </ul>

      <div class="lvl-rulesCard sk-dark">
        <div class="lvl-rulesHd">
          <span class="sk sk-circle lvl-skelRuleIcon"></span>
          <span class="sk lvl-skelRuleHd"></span>
        </div>
        <div class="lvl-skelRules">
          <div v-for="n in 4" :key="n" class="lvl-skelRuleRow">
            <span class="sk sk-circle"></span>
            <span class="sk"></span>
          </div>
        </div>
      </div>

      <span class="sk sk-dark lvl-skelDisclose"></span>
    </template>

    <div v-else-if="error" class="lvl-hero">
      <p class="lvl-state">
        {{ error }}
        <br />
        <button type="button" class="lvl-retry" @click="fetchLevels(true)">Try again</button>
      </p>
    </div>

    <template v-else>
      <!-- Who you are, and the badge this exact level earns -->
      <div class="lvl-hero">
        <div class="lvl-identity">
          <span class="lvl-avatarWrap" :class="{ 'no-frame': !currentFrame }">
            <img class="lvl-avatar" :src="avatarSrc" :alt="user.name || 'Avatar'" @error="onAvatarError" />
            <img v-if="currentFrame" class="lvl-frame" :src="currentFrame" alt="" aria-hidden="true" />
          </span>

          <span class="lvl-who">
            <span class="lvl-name">{{ user.name || '—' }}</span>
            <span class="lvl-badgeRow">
              <!-- The plate the backend issues carries the tier colour and
                   symbol but no digits — the app draws the number over it, so
                   the page does the same rather than showing a blank pill. -->
              <span v-if="currentIcon" class="lvl-plate is-lg">
                <img class="lvl-plateImg" :src="currentIcon" alt="" />
                <span class="lvl-plateNo lvl-num">{{ user.current_level ?? '' }}</span>
              </span>
              <span class="lvl-badgeText">{{ tierLabel }}</span>
            </span>
          </span>
        </div>

        <div class="lvl-progress">
          <div class="lvl-toGo">
            <span class="lvl-toGoVal lvl-num">{{ toLevelUpLabel }}</span>
            <span class="lvl-toGoCap">{{ unitLabel }} to level up</span>
          </div>

          <div class="lvl-track">
            <div class="lvl-fill" :style="{ width: progressPercentage + '%' }"></div>
          </div>

          <div class="lvl-ends">
            <span class="is-now">Lv {{ user.current_level ?? '-' }}</span>
            <span>Lv {{ user.next_level ?? '-' }}</span>
          </div>
        </div>
      </div>

      <p v-if="!levels.length" class="lvl-state">No level data available.</p>

      <template v-else>
        <!-- The badges you collect on the way up. One tile per tier, because
             every level inside a tier wears the same plate — the backend
             serves the identical artwork under a per-level URL, so drawing
             all 60 would be the same six pictures fetched sixty times. -->
        <div class="lvl-sectionHd">
          <h2>Level badges</h2>
          <span>{{ tiers.length }} tiers</span>
        </div>

        <ul class="lvl-grid">
          <li v-for="tier in tiers" :key="tier.key" class="lvl-tier" :class="tier.state">
            <span class="lvl-plate is-grid">
              <img v-if="tier.icon" class="lvl-plateImg" :src="tier.icon" alt=""
                loading="lazy" decoding="async" />
              <span class="lvl-plateNo lvl-num">{{ tier.from }}</span>
            </span>
            <!-- Earned or locked sits beside the range rather than in the
                 corner: the plate fills the tile, so a corner badge lands on
                 top of the artwork. -->
            <span class="lvl-tierMeta">
              <svg v-if="tier.state === 'is-done'" class="lvl-tierTick" width="11" height="11"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4"
                stroke-linecap="round" stroke-linejoin="round" aria-label="Earned">
                <path d="M5 12.5l4.5 4.5L19 7" />
              </svg>
              <svg v-else-if="tier.state === 'is-locked'" class="lvl-tierLock" width="10" height="10"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
                stroke-linecap="round" stroke-linejoin="round" aria-label="Locked">
                <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" />
                <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
              </svg>
              <span class="lvl-tierRange">Lv {{ tier.from }}–{{ tier.to }}</span>
            </span>
          </li>
        </ul>

        <!-- How the level is earned. Numbered the same way the rules read on
             the reward pages, so the two feel like one app. -->
        <div class="lvl-rulesCard">
          <div class="lvl-rulesHd">
            <span class="lvl-rulesIcon" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
                <path d="M14 3v6h6" />
                <path d="M9 13.5h6" />
                <path d="M9 17h4" />
              </svg>
            </span>
            <h3>{{ tierLabel }} rules</h3>
          </div>
          <ul class="lvl-rules">
            <li v-for="(rule, i) in ruleList" :key="i"><span>{{ rule }}</span></li>
          </ul>
        </div>

        <!-- The level-by-level table is the reference material, not the point
             of the page, so it stays folded away until asked for. Collapsed
             also keeps its lazy images from loading at all. -->
        <button type="button" class="lvl-disclose" :aria-expanded="String(showAll)"
          @click="showAll = !showAll">
          <span>{{ showAll ? 'Hide' : 'View' }} all {{ ladder.length }} levels</span>
          <svg class="lvl-chev" :class="{ 'is-open': showAll }" width="16" height="16"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <ul v-show="showAll" class="lvl-ladder">
          <li class="lvl-ladderHd">
            <span>Level</span>
            <span>{{ unitLabel }} needed</span>
          </li>

          <li v-for="step in ladder" :key="step.id" class="lvl-step" :class="step.state">
            <span class="lvl-plate">
              <img v-if="step.icon" class="lvl-plateImg" :src="step.icon" alt=""
                loading="lazy" decoding="async" />
              <span class="lvl-plateNo lvl-num">{{ step.level }}</span>
            </span>

            <svg v-if="step.state === 'is-done'" class="lvl-tick" width="16" height="16"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12.5l4.5 4.5L19 7" />
            </svg>
            <span v-else-if="step.state === 'is-current'" class="lvl-nowTag">You are here</span>

            <span class="lvl-stepAmt lvl-num">{{ step.amountLabel }}</span>
          </li>
        </ul>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import '@/assets/css/skeleton.css';
import './MyLevelRefresh.css';
import defaultUserImage from '../../assets/images/default_profile.webp';

/* One board serves both tabs. Rich and charm differed only in which points
 * field they read and their copy, but shipped as two components each holding
 * a second child component — and every one of those four fetched the same
 * endpoint, so opening a tab fired the request four times. One board plus the
 * guard in fetchLevels brings that to one. */

/* Levels share one badge per block of ten: the API hands out a separate icon
 * URL for every level, but the files are byte-identical within a block (level
 * 1 and level 7 hash the same). The grid shows one tile per block. */
const TIER_SIZE = 10;

/* The rules are deliberately limited to what the page itself can show: where
 * the points come from, that they add up, that every level has a target in
 * the list below, and what happens when you reach it. Nothing about expiry,
 * resets or bonus multipliers — that copy has to come from the product side. */
const CONFIG = {
  rich_level: {
    pointsKey: 'redeem_point',
    unit: 'diamonds',
    tier: 'Rich level',
    rule: 'Your level is counted from the diamonds you have recharged.',
    rules: [
      'Your Rich level is counted from the diamonds you recharge.',
      'Every recharge adds to your total, so the more you top up the faster you climb.',
      'Each level has its own diamond target — the full list is below.',
      'Reach the next target and you move up a level, and the badge changes with it.',
    ],
  },
  charm_level: {
    pointsKey: 'earning_points',
    unit: 'beans',
    tier: 'Charm level',
    rule: 'Your level is counted from the beans you have earned.',
    rules: [
      'Your Charm level is counted from the beans you earn.',
      'Every bean you earn adds to your total, so the more you earn the faster you climb.',
      'Each level has its own bean target — the full list is below.',
      'Reach the next target and you move up a level, and the badge changes with it.',
    ],
  },
};

export default {
  name: 'LevelBoard',

  props: {
    type: { type: String, required: true },
  },

  data() {
    return {
      loading: true,
      error: '',
      user: {},
      levels: [],
      avatarFailed: false,
      defaultUserImage,
      // The per-level table is reference material; folded away by default.
      showAll: false,
      // Guards against the double fetch: mounted() fires one request and the
      // token watcher fires another as the router finishes resolving params.
      lastFetchKey: null,
    };
  },

  computed: {
    config() {
      return CONFIG[this.type] || CONFIG.rich_level;
    },

    unitLabel() {
      return this.config.unit;
    },

    tierLabel() {
      return this.config.tier;
    },

    ruleText() {
      return this.config.rule;
    },

    ruleList() {
      return this.config.rules || [];
    },

    points() {
      return this.user?.[this.config.pointsKey];
    },

    avatarSrc() {
      if (this.avatarFailed) return this.defaultUserImage;
      return this.user?.profile_images?.[0]?.image_name || this.defaultUserImage;
    },

    currentStep() {
      // Read off the deduped ladder so the hero badge matches the rung the
      // list marks as yours.
      return this.ladder.find((row) => row.state === 'is-current') || null;
    },

    currentIcon() {
      return this.currentStep?.icon || null;
    },

    currentFrame() {
      return this.currentStep?.frame || null;
    },

    // Unchanged from the two components this replaces — the figures behind it
    // are the backend's, so the arithmetic stays exactly as it shipped.
    progressPercentage() {
      const points = this.points;
      const next = this.user?.next_level_amount;

      if (this.user?.next_level === 0) return 100;
      if (!points || !next) return 0;

      return Math.min(100, Math.max(0, 100 - ((next - points) * 100) / next));
    },

    toLevelUpLabel() {
      const points = this.points;
      const next = this.user?.next_level_amount;
      if (typeof next !== 'number' || typeof points !== 'number') return '—';
      const value = next !== 0 ? next - points : next;
      return this.formatNumber(value);
    },

    ladder() {
      const level = Number(this.user?.current_level);

      // The charm list arrived with two rows for level 1 (ids 22 and 14, at 1
      // and 10 beans), which drew the rung twice and put "You are here" on
      // both. The backend has since dropped the spare row, but the ladder
      // collapses duplicates and sorts anyway so a repeat cannot break it: the
      // higher threshold wins, independent of the order the API returns.
      const byLevel = new Map();
      this.levels.forEach((row) => {
        const key = Number.isFinite(Number(row.level)) ? Number(row.level) : `id-${row.id}`;
        const kept = byLevel.get(key);
        if (!kept || Number(row.amount) > Number(kept.amount)) byLevel.set(key, row);
      });

      return [...byLevel.values()]
        .sort((a, b) => Number(a.level) - Number(b.level))
        .map((row) => {
          const rowLevel = Number(row.level);
          let state = 'is-locked';
          if (Number.isFinite(level)) {
            if (rowLevel < level) state = 'is-done';
            else if (rowLevel === level) state = 'is-current';
          }
          return {
            ...row,
            state,
            amountLabel: this.formatNumber(row.amount),
          };
        });
    },

    // One tile per block of ten levels, keyed off the block a level falls in
    // rather than the row's position, so a gap in the list cannot shift the
    // ranges. A trailing part-block reports its real last level.
    tiers() {
      const level = Number(this.user?.current_level);
      const blocks = new Map();

      this.ladder.forEach((row) => {
        const rowLevel = Number(row.level);
        if (!Number.isFinite(rowLevel)) return;

        const index = Math.floor((rowLevel - 1) / TIER_SIZE);
        const block = blocks.get(index);
        if (!block) {
          blocks.set(index, {
            key: index,
            from: index * TIER_SIZE + 1,
            to: rowLevel,
            icon: row.icon || null,
          });
          return;
        }
        block.to = Math.max(block.to, rowLevel);
        if (!block.icon) block.icon = row.icon || null;
      });

      return [...blocks.values()]
        .sort((a, b) => a.from - b.from)
        .map((block) => {
          let state = 'is-locked';
          if (Number.isFinite(level)) {
            if (level > block.to) state = 'is-done';
            else if (level >= block.from) state = 'is-current';
          }
          return { ...block, state };
        });
    },
  },

  mounted() {
    this.fetchLevels();
  },

  watch: {
    // Wrapped, not passed by name: a watcher hands the handler
    // (newValue, oldValue), which would land in `force` and defeat the guard.
    '$route.params.token'() {
      this.fetchLevels();
    },
  },

  methods: {
    async fetchLevels(force = false) {
      const tokenParam = this.$route.params.token || '';
      const token = tokenParam.includes('=') ? tokenParam.split('=')[0] : tokenParam;

      const key = `${this.type}|${token}`;
      if (!force && key === this.lastFetchKey) return;
      this.lastFetchKey = key;

      this.loading = true;
      this.error = '';

      try {
        const { data } = await axios.get(`api/user-rich-charm-level-details?type=${this.type}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.user = data?.result?.data || {};
        this.levels = Array.isArray(data?.result?.levellist) ? data.result.levellist : [];
        this.avatarFailed = false;
      } catch (err) {
        console.error('Error fetching level details:', err);
        this.user = {};
        this.levels = [];
        // Let Try again re-run the same key.
        this.lastFetchKey = null;
        this.error = 'Could not load your level. Check your connection and try again.';
      } finally {
        this.loading = false;
      }
    },

    onAvatarError(e) {
      this.avatarFailed = true;
      if (e?.target) {
        e.target.onerror = null;
        e.target.src = this.defaultUserImage;
      }
    },

    formatNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-IN') : '—';
    },
  },
};
</script>
